// src/pages/PreInscricoes/ListaPreInscricoesPorClasseCursoPage.js

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Modal,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PreInscricaoCard from '../../components/PreInscricoes/PreInscricaoCard';
import { listarPreInscricoesPorClasseOuCurso } from '../../services/preInscricaoService';
import { CSVLink } from 'react-csv';

const ListaPreInscricoesPorClasseCursoPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ID da classe ou curso
  const location = useLocation();
  const [tipo, setTipo] = useState('curso'); // Pode ser 'classe' ou 'curso'
  const [preInscricoes, setPreInscricoes] = useState([]); // Inicializado como array
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Estado para armazenar a mensagem de confirmação
  const [mensagemConfirmacao, setMensagemConfirmacao] = useState('');
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    // Verifica se há estado vindo da pré-inscrição
    if (location.state && location.state.mensagemConfirmacao) {
      setMensagemConfirmacao(location.state.mensagemConfirmacao);
      setOpenModal(true);
    }

    fetchPreInscricoes(currentPage, itemsPerPage, tipo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, tipo]);

  const fetchPreInscricoes = async (page, limit, tipo) => {
    setLoading(true);
    try {
      const data = await listarPreInscricoesPorClasseOuCurso(id, tipo, page, limit);
      console.log('Dados recebidos da API:', data);
      setPreInscricoes(Array.isArray(data.preInscricoes) ? data.preInscricoes : []);
      setTotalPages(Math.ceil(data.totalItems / limit));
    } catch (error) {
      console.error('Erro ao listar pré-inscrições por classe/curso:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.mensagem || 'Erro ao buscar pré-inscrições.',
        severity: 'error',
      });
      setPreInscricoes([]); // Garante que seja um array
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Volta para a página anterior
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reseta para a primeira página
  };

  const handleTipoChange = (event) => {
    setTipo(event.target.value);
    setCurrentPage(1); // Reseta para a primeira página
  };

  // Função unificada para fechar o Snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const headers = [
    { label: 'ID', key: 'id' },
    { label: 'Nome do Estudante', key: 'estudante.nome' },
    { label: 'Email', key: 'estudante.email' },
    { label: 'Campanha', key: 'campanha.nome' },
    { label: 'Descrição da Classe', key: 'classe.descricao' }, // Usando 'descricao'
    { label: 'Nome do Curso', key: 'curso.nome' },
    { label: 'Status', key: 'status' },
    { label: 'Data da Pré-inscrição', key: 'createdAt' },
  ];

  const csvData = preInscricoes.map((preInscricao) => ({
    id: preInscricao.id,
    'estudante.nome': preInscricao.estudante?.nome || 'N/A',
    'estudante.email': preInscricao.estudante?.email || 'N/A',
    'campanha.nome': preInscricao.campanha?.nome || 'N/A',
    'classe.descricao': preInscricao.classe?.descricao || 'N/A', // Usando 'descricao'
    'curso.nome': preInscricao.curso?.nome || 'N/A',
    status: preInscricao.status,
    createdAt: new Date(preInscricao.createdAt).toLocaleDateString(),
  }));

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Pré-Inscrições por {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
      </Typography>
      <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Button variant="contained" color="primary" onClick={handleBack}>
          Voltar
        </Button>
        <CSVLink
          data={csvData}
          headers={headers}
          filename={`preinscricoes-${tipo}-${id}.csv`}
          style={{ textDecoration: 'none' }}
        >
          <Button variant="contained" color="success">
            Exportar como CSV
          </Button>
        </CSVLink>
      </Box>
      {/* Filtro de Tipo */}
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="tipo-select-label">Tipo</InputLabel>
          <Select
            labelId="tipo-select-label"
            value={tipo}
            onChange={handleTipoChange}
            label="Tipo"
          >
            <MenuItem value="curso">Curso</MenuItem>
            <MenuItem value="classe">Classe</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : Array.isArray(preInscricoes) && preInscricoes.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {preInscricoes.map((preInscricao) => (
              <Grid item key={preInscricao.id} xs={12} sm={6} md={4}>
                <PreInscricaoCard preInscricao={preInscricao} />
                {/* Dentro de PreInscricaoCard, use preInscricao.classe.descricao */}
              </Grid>
            ))}
          </Grid>
          {/* Paginação */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
            <FormControl variant="outlined" size="small">
              <InputLabel id="items-per-page-label">Itens por Página</InputLabel>
              <Select
                labelId="items-per-page-label"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                label="Itens por Página"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </>
      ) : (
        <Typography variant="body1">Nenhuma pré-inscrição encontrada.</Typography>
      )}
      {/* Modal para mensagem de confirmação */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-mensagem-confirmacao"
        aria-describedby="modal-mensagem-confirmacao-descricao"
      >
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bgcolor="background.paper"
          boxShadow={24}
          p={4}
          borderRadius={2}
          maxWidth={600}
          width="90%"
        >
          <Typography id="modal-mensagem-confirmacao" variant="h6" component="h2" gutterBottom>
            Confirmação de Pré-inscrição
          </Typography>
          <Typography id="modal-mensagem-confirmacao-descricao" sx={{ mt: 2 }}>
            {mensagemConfirmacao}
          </Typography>
          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={handleCloseModal}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose} // Atualizado
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListaPreInscricoesPorClasseCursoPage;
